/**
 * Clients and their rates.
 *
 * Rates live here rather than on the project so a change is made in one place —
 * but note what it does NOT do: existing time entries keep the rate they
 * snapshotted at creation. Editing a rate changes what future work bills at,
 * never what past work billed at. That is the whole point of the snapshot, and
 * the page says so rather than leaving you to discover it.
 */

import { useMemo, useState } from "react";

import { card } from "@/components/atoms";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/context/ToastContext";
import { useChrome } from "@/context/ChromeContext";
import { useInheritClients, useProjects, useSaveProject } from "@/hooks/useData";
import { resolveClient, useClientMutations, useClients, useTimeEntries } from "@/hooks/useTime";
import { appMode } from "@/lib/pocketbase";
import { hours, money, totalsOf } from "@/lib/timeRollup";
import { useIsMobile } from "@/lib/useMediaQuery";
import type { Client, Project } from "@/lib/types";
import { MISSING_TERMS_LABEL, isFlatFee, isInternalProject, missingTerms, projectRate } from "@/lib/billing";

export function Clients() {
  const { data: clients = [] } = useClients();
  const { data: projects = [] } = useProjects();
  const { data: entries = [] } = useTimeEntries();
  const { saveClient, deleteClient } = useClientMutations();
  const saveProject = useSaveProject();
  const inheritClients = useInheritClients();
  /** " and 2 sub-projects", after a link carried down to them. */
  const subsNote = (n: number) => (n ? ` and ${n} sub-project${n === 1 ? "" : "s"}` : "");
  const toast = useToast();
  const mobile = useIsMobile();

  const [editing, setEditing] = useState<Partial<Client> | null>(null);
  const [confirm, setConfirm] = useState<Client | null>(null);

  /** Projects pointing at each client, and what has been logged against them. */
  const usage = useMemo(() => {
    const map = new Map<string, { projects: string[]; seconds: number; amount: number; unbilled: number }>();
    for (const c of clients) map.set(c.id, { projects: [], seconds: 0, amount: 0, unbilled: 0 });
    for (const p of projects) {
      const c = resolveClient(p, clients);
      if (c) map.get(c.id)?.projects.push(p.name);
    }
    for (const [id, row] of map) {
      const t = totalsOf(entries.filter((e) => e.client === id), projects);
      row.seconds = t.seconds;
      row.amount = t.amount;
      row.unbilled = t.unbilledAmount;
    }
    return map;
  }, [clients, projects, entries]);

  /**
   * Active projects with no client at all. Left out: internal and personal
   * projects, which have none by design, and projects that hold sub-projects,
   * which are groups. A project naming a client that doesn't exist is in
   * `unmatched` below instead.
   */
  const unassigned = useMemo(() => {
    const parents = new Set(projects.map((p) => p.parent).filter(Boolean));
    return projects.filter((p) =>
      p.kind !== "inbox" && p.status !== "archived" && !isInternalProject(p) && p.category !== "personal"
      && !parents.has(p.id) && !(p.client ?? "").trim() && !resolveClient(p, clients));
  }, [projects, clients]);

  async function assign(projectId: string, client: Client) {
    try {
      await saveProject.mutateAsync({ id: projectId, client: client.name, clientRef: client.id } as never);
      toast(`Linked to ${client.name}${subsNote(await inheritClients())}`);
    } catch {
      toast("Could not update that project");
    }
  }

  /**
   * Projects naming a client with no record here. Kept per project rather than
   * per name: "one project names a client" states the rule but never says which
   * project, so it cannot be acted on without going to look.
   */
  const unmatched = useMemo(() => {
    const names = new Set(clients.map((c) => c.name.trim().toLowerCase()));
    return projects
      .filter((p) => {
        const name = (p.client ?? "").trim();
        return name && !names.has(name.toLowerCase());
      })
      .map((p) => ({ project: p.name, client: (p.client ?? "").trim() }));
  }, [clients, projects]);

  /**
   * `pendingLinks` exists because a new client has no id until it is saved, so
   * projects picked in the modal cannot be written yet. They are applied here
   * against the saved record, which makes "add the client and attach its
   * projects" one action rather than save-then-find-it-again.
   */
  async function save(draft: Partial<Client>, pendingLinks: string[]) {
    if (!draft.name?.trim()) { toast("A name is required"); return; }
    if (appMode === "demo") { toast("Not available in demo mode"); return; }
    try {
      const saved = await saveClient.mutateAsync({ ...draft, name: draft.name.trim() });
      for (const projectId of pendingLinks) {
        await saveProject.mutateAsync({ id: projectId, client: saved.name, clientRef: saved.id } as never);
      }
      const subs = pendingLinks.length ? await inheritClients() : 0;
      toast((draft.id ? "Client saved" : "Client added") + (subs ? ` · ${subs} sub-project${subs === 1 ? "" : "s"} linked too` : ""));
      setEditing(null);
    } catch {
      toast("Save failed");
    }
  }

  useChrome({ title: "Clients", subtitle: "Rates and contact details" }, []);

  return (
    <div style={{ padding: mobile ? "16px 16px 90px" : "20px 36px 90px", maxWidth: 900 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        {/* No heading here — the app header already names the page. */}
        <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
          Rates used for new time entries; a project can set its own rate to override its
          client's. Todoist has no concept of a client, so this is Project Hub's own.
        </div>
        <button onClick={() => setEditing({ currency: "USD" })} style={{ ...primaryBtn, marginLeft: "auto" }}>
          Add client
        </button>
      </div>

      {unmatched.length > 0 && (
        <div style={{
          ...card, padding: 14, marginBottom: 14,
          background: "var(--warnTint)", border: "1px solid var(--warnLine)",
        }}>
          {unmatched.map(({ project, client }) => (
            <div key={`${project}:${client}`} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: "var(--ink2)", flex: "1 1 340px", minWidth: 0 }}>
                <strong>{project}</strong> names <strong>{client}</strong> as its client, and there
                is no such client here. Its hours are still recorded — they just have no rate, so
                they bill nothing.
              </span>
              <button onClick={() => setEditing({ name: client, currency: "USD" })} style={ghostBtn}>
                Add “{client}”
              </button>
            </div>
          ))}
        </div>
      )}

      {unassigned.length > 0 && clients.length > 0 && (
        <div style={{ ...card, padding: 16, marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 15, fontWeight: 500 }}>Unassigned projects</div>
            <div style={{ fontSize: 12.5, color: "var(--muted)" }}>No client yet. Pick one to link it.</div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 9 }}>
            {unassigned.map((p) => (
              <span key={p.id} style={chipStyle(p)}>
                <ProjectDot project={p} />
                {p.name}
                <select
                  value=""
                  onChange={(e) => { const c = clients.find((x) => x.id === e.target.value); if (c) void assign(p.id, c); }}
                  title={`Link ${p.name} to a client`}
                  style={{ border: "none", background: "none", color: "var(--accent)", fontSize: 12, cursor: "pointer", padding: 0 }}
                >
                  <option value="">+ client</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </span>
            ))}
          </div>
        </div>
      )}

      {clients.length === 0 && (
        <div style={{ ...card, padding: 24, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
          No clients yet. Until one exists with a rate, time is logged with no amount —
          the hours are still recorded.
        </div>
      )}

      {clients.map((c) => {
        const use = usage.get(c.id);
        return (
          <div key={c.id} style={{ ...card, padding: 16, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: 15, fontWeight: 500 }}>{c.name}</div>
              <div style={{ fontSize: 13, color: "var(--ink3)" }}>
                {/* Optional: only hourly work without a rate of its own uses it,
                    and a project that needs one says so on its chip. */}
                {c.defaultRate ? `${money(c.defaultRate, c.currency)}/h default` : null}
              </div>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button onClick={() => setEditing(c)} style={ghostBtn}>Edit</button>
                <button onClick={() => setConfirm(c)} style={ghostBtn}>Delete</button>
              </div>
            </div>
            <ProjectLinks
              client={c}
              projects={projects.filter((p) => p.kind !== "inbox")}
              clients={clients}
              onLink={async (projectId, link) => {
                try {
                  // Both halves: the relation is what rates resolve through,
                  // the text is what the rest of the app displays. A project
                  // has at most one client, so linking it here moves it.
                  await saveProject.mutateAsync({
                    id: projectId,
                    client: link ? c.name : "",
                    clientRef: link ? c.id : null,
                  } as never);
                  // Unlinking leaves the subs as they are: they may be meant
                  // for this client on their own.
                  toast(link ? `Linked to ${c.name}${subsNote(await inheritClients())}` : "Unlinked");
                } catch {
                  toast("Could not update that project");
                }
              }}
            />
            {use && use.seconds > 0 && (
              <div style={{ fontSize: 12.5, color: "var(--ink3)", marginTop: 4 }}>
                {hours(use.seconds)} h logged
                {/* The row already says "no rate set"; repeating it as $0.00
                    invites the conclusion that the work was worthless. */}
                {use.amount > 0 && ` · ${money(use.amount, c.currency)} billable to date`}
                {use.unbilled > 0 && (
                  <span style={{ color: "var(--accent)" }}> · {money(use.unbilled, c.currency)} not yet invoiced</span>
                )}
              </div>
            )}
            {c.notes && (
              <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 6 }}>{c.notes}</div>
            )}
          </div>
        );
      })}

      {editing && (
        <ClientModal
          draft={editing}
          projects={projects.filter((p) => p.kind !== "inbox")}
          clients={clients}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={(links) => void save(editing, links)}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={`Delete ${confirm.name}?`}
          message={
            "Time already logged keeps the rate it recorded — deleting a client never changes " +
            "past entries or their amounts. Projects naming this client will simply stop " +
            "resolving a rate for new work."
          }
          confirmLabel="Delete"
          danger
          onConfirm={async () => {
            if (appMode === "demo") { toast("Not available in demo mode"); setConfirm(null); return; }
            try { await deleteClient.mutateAsync(confirm.id); toast("Client deleted"); }
            catch { toast("Delete failed"); }
            setConfirm(null);
          }}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

/** Active projects first, archived after; each keeps its order otherwise. */
function activeFirst(list: Project[]): Project[] {
  return [...list.filter((p) => p.status !== "archived"), ...list.filter((p) => p.status === "archived")];
}

/** A linked project's chip; an archived one is dimmed. */
function chipStyle(p: Project): React.CSSProperties {
  const archived = p.status === "archived";
  return {
    display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12,
    padding: "3px 8px", borderRadius: 999,
    background: archived ? "none" : "var(--tint)",
    border: archived ? "1px dashed var(--line2)" : "1px solid transparent",
    color: archived ? "var(--muted)" : "var(--ink2)",
  };
}

/** The project's own colour, as in the sidebar. An archived one has none. */
function ProjectDot({ project }: { project: Project }) {
  if (project.status === "archived") return null;
  return <span style={{ width: 7, height: 7, borderRadius: "50%", background: project.color || "var(--good)", flexShrink: 0 }} />;
}

function ArchivedTag() {
  return <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.3px", color: "var(--muted2)" }}>Archived</span>;
}

/**
 * Which projects belong to this client, editable from here.
 *
 * The link previously existed only inside the project's own edit modal, which
 * made the Clients page look decorative — you could set a rate but never see
 * it reach anything. A project has at most one client, so picking it here also
 * takes it off whichever client had it.
 */
function ProjectLinks({ client, projects, clients, onLink }: {
  client: Client;
  projects: Project[];
  clients: Client[];
  onLink: (projectId: string, link: boolean) => void | Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  // Archived projects stay listed, after the active ones, so the client's
  // history is all here; they just aren't offered for linking.
  const mine = activeFirst(projects.filter((p) => resolveClient(p, clients)?.id === client.id));
  const available = projects.filter((p) => p.status !== "archived" && resolveClient(p, clients)?.id !== client.id);

  return (
    <div style={{ marginTop: 9 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {mine.length === 0 && (
          <span style={{ fontSize: 12, color: "var(--muted)" }}>No projects linked yet.</span>
        )}
        {mine.map((p) => (
          <span key={p.id} style={chipStyle(p)}>
            <ProjectDot project={p} />
            {p.name}
            {p.status === "archived" && <ArchivedTag />}
            {/* An hourly project with its own rate says so; the rest bill at the
                client's. Fees and retainers aren't charged by the hour, so they
                show nothing unless their terms are missing. */}
            {!isFlatFee(p) && !isInternalProject(p) && projectRate(p) !== null && (
              <span className="mono" style={{ color: "var(--muted)" }}>· {money(projectRate(p)!, p.currency || client.currency)}/h</span>
            )}
            {p.status !== "archived" && missingTerms(p, client.defaultRate) && (
              <span style={{ color: "var(--warn)" }}>· {MISSING_TERMS_LABEL[missingTerms(p, client.defaultRate)!]}</span>
            )}
            <button
              onClick={() => void onLink(p.id, false)}
              title={`Unlink ${p.name}`}
              style={{ border: "none", background: "none", color: "var(--muted3)", cursor: "pointer", padding: 0, fontSize: 12 }}
            >✕</button>
          </span>
        ))}

        {adding ? (
          <select
            autoFocus
            defaultValue=""
            onChange={(e) => { if (e.target.value) void onLink(e.target.value, true); setAdding(false); }}
            onBlur={() => setAdding(false)}
            style={{
              padding: "4px 8px", borderRadius: 999, fontSize: 12,
              border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
            }}
          >
            <option value="">— Pick a project</option>
            {available.map((p) => {
              const held = resolveClient(p, clients);
              return (
                <option key={p.id} value={p.id}>
                  {p.name}{held ? ` (currently ${held.name})` : ""}
                </option>
              );
            })}
          </select>
        ) : (
          <button
            onClick={() => setAdding(true)}
            style={{
              fontSize: 12, padding: "3px 10px", borderRadius: 999,
              border: "1px dashed var(--line2)", background: "none", color: "var(--ink3)", cursor: "pointer",
            }}
          >+ Link a project</button>
        )}
      </div>
    </div>
  );
}

function ClientModal({ draft, projects, clients, onChange, onSave, onCancel }: {
  draft: Partial<Client>;
  projects: Project[];
  clients: Client[];
  onChange: (d: Partial<Client>) => void;
  onSave: (pendingLinks: string[]) => void;
  onCancel: () => void;
}) {
  // Already-linked projects for a saved client; a queue for a new one.
  const [links, setLinks] = useState<string[]>(() =>
    draft.id ? projects.filter((p) => resolveClient(p, clients)?.id === draft.id).map((p) => p.id) : [],
  );
  const [picking, setPicking] = useState(false);
  const linked = activeFirst(projects.filter((p) => links.includes(p.id)));
  const available = projects.filter((p) => p.status !== "archived" && !links.includes(p.id));
  return (
    <div onClick={onCancel} style={{
      position: "fixed", inset: 0, background: "var(--scrim)", zIndex: 50,
      display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "60px 20px", overflowY: "auto",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 460, maxWidth: "100%", background: "var(--raised)", borderRadius: 14,
        padding: "24px 26px", border: "1px solid var(--overlayLine)", boxShadow: "var(--shadowOverlay)",
      }}>
        <h2 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 500 }}>
          {draft.id ? "Edit client" : "New client"}
        </h2>

        <label style={label}>
          Name
          <input
            autoFocus value={draft.name ?? ""} style={input}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            placeholder="Riverside Health System"
          />
        </label>
        <div style={{ fontSize: 11, color: "var(--muted)", margin: "-6px 0 12px" }}>
          Projects can also be linked by this exact name, so it is worth matching what the
          project's client field says.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={label}>
            Default hourly rate (optional)
            <input
              // PocketBase stores a blank rate as 0; show it blank.
              type="number" min={0} step={5} value={draft.defaultRate || ""} style={input}
              onChange={(e) => onChange({ ...draft, defaultRate: e.target.value === "" ? undefined : Number(e.target.value) })}
              placeholder="185"
            />
          </label>
          <label style={label}>
            Currency
            <input
              value={draft.currency ?? "USD"} style={input}
              onChange={(e) => onChange({ ...draft, currency: e.target.value.toUpperCase().slice(0, 3) })}
            />
          </label>
        </div>
        <div style={{ fontSize: 11, color: "var(--muted)", margin: "-6px 0 12px" }}>
          Used for hourly projects without a rate of their own, and for time logged to this client with no project.
          Fixed-fee and retainer projects don't use it.
        </div>

        <label style={label}>
          Notes
          <textarea
            value={draft.notes ?? ""} style={{ ...input, minHeight: 62, resize: "vertical" }}
            onChange={(e) => onChange({ ...draft, notes: e.target.value })}
            placeholder="Billing terms, PO number, anything you need at invoice time"
          />
        </label>

        {draft.id && (
          <div style={{
            fontSize: 11.5, color: "var(--ink3)", background: "var(--tint)",
            border: "1px solid var(--line)", borderRadius: 8, padding: "9px 11px", margin: "4px 0 14px",
          }}>
            Changing the rate affects <strong>new</strong> time entries only. Every entry already
            logged keeps the rate it recorded, so past totals and invoices never move.
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11.5, color: "var(--muted)", marginBottom: 6 }}>Projects</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {linked.length === 0 && !picking && (
              <span style={{ fontSize: 12, color: "var(--muted2)" }}>None yet.</span>
            )}
            {linked.map((p) => (
              <span key={p.id} style={chipStyle(p)}>
                <ProjectDot project={p} />
                {p.name}
                {p.status === "archived" && <ArchivedTag />}
                <button
                  onClick={() => setLinks((v) => v.filter((id) => id !== p.id))}
                  title={`Unlink ${p.name}`}
                  style={{ border: "none", background: "none", color: "var(--muted3)", cursor: "pointer", padding: 0, fontSize: 12 }}
                >✕</button>
              </span>
            ))}
            {picking ? (
              <select
                autoFocus defaultValue=""
                onChange={(e) => { if (e.target.value) setLinks((v) => [...v, e.target.value]); setPicking(false); }}
                onBlur={() => setPicking(false)}
                style={{ padding: "4px 8px", borderRadius: 999, fontSize: 12, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)" }}
              >
                <option value="">— Pick a project</option>
                {available.map((p) => {
                  // A project has at most one client, so picking it moves it —
                  // this annotation is the only warning of that.
                  const held = resolveClient(p, clients);
                  return (
                    <option key={p.id} value={p.id}>
                      {p.name}{held && held.id !== draft.id ? ` (currently ${held.name})` : ""}
                    </option>
                  );
                })}
              </select>
            ) : (
              <button
                onClick={() => setPicking(true)}
                style={{ fontSize: 12, padding: "3px 10px", borderRadius: 999, border: "1px dashed var(--line2)", background: "none", color: "var(--ink3)", cursor: "pointer" }}
              >+ Link a project</button>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={onCancel} style={ghostBtn}>Cancel</button>
          <button onClick={() => onSave(links)} style={primaryBtn}>Save</button>
        </div>
      </div>
    </div>
  );
}

const label: React.CSSProperties = { display: "block", fontSize: 11.5, color: "var(--muted)", marginBottom: 12 };
const input: React.CSSProperties = {
  width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 13, marginTop: 5,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
};
const primaryBtn: React.CSSProperties = {
  padding: "8px 16px", borderRadius: 999, border: "none",
  background: "var(--accent)", color: "var(--onAccent)", fontSize: 12.5, cursor: "pointer",
};
const ghostBtn: React.CSSProperties = {
  padding: "7px 14px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer",
};
